<template>
  <a-drawer
    width="35%"
    :label-col="4"
    :wrapper-col="14"
    :visible="open"
    @close="onClose"
    :destroyOnClose="true">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-form-model-item :label="$t('views.systemUser.form.nickName')" prop="nickName">
        <a-input v-model="form.nickName" :placeholder="$t('views.systemUser.form.placeholderInput')" :maxLength="30" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.dept')" prop="deptId">
        <a-tree-select
          v-model="form.deptId"
          style="width: 100%"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
          :tree-data="deptOptions"
          :placeholder="$t('views.systemUser.form.placeholderSelect')"
          :replaceFields="replaceFields"
          tree-default-expand-all
        >
        </a-tree-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.phoneNumber')" prop="phoneNumber">
        <a-input v-model="form.phoneNumber" :placeholder="$t('views.systemUser.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.email')" prop="email">
        <a-input v-model="form.email" :placeholder="$t('views.systemUser.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.userName')" prop="userName" v-if="form.id == undefined">
        <a-input v-model="form.userName" :placeholder="$t('views.systemUser.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.password')" prop="password" v-if="form.id == undefined">
        <a-input-password v-model="form.password" :placeholder="$t('views.systemUser.form.placeholderInput')" :maxLength="20" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.sex')" prop="sex">
        <a-radio-group v-model="form.sex" button-style="solid">
          <a-radio-button v-for="(d, index) in localizedSexOptions" :key="index" :value="parseInt(d.value)">{{ d.label }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.status')" prop="status">
        <a-radio-group v-model="form.status" button-style="solid">
          <a-radio-button v-for="(d, index) in localizedStatusOptions" :key="index" :value="parseInt(d.value)">{{ d.label }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.post')" prop="postIds">
        <a-select
          mode="multiple"
          v-model="form.postIds"
          :placeholder="$t('views.systemUser.form.placeholderSelect')"
        >
          <a-select-option v-for="(d, index) in postOptions" :key="index" :value="d.id">
            {{ d.postName }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.role')" prop="roleIds">
        <a-select
          mode="multiple"
          v-model="form.roleIds"
          :placeholder="$t('views.systemUser.form.placeholderSelect')"
        >
          <a-select-option v-for="(d, index) in roleOptions" :key="index" :value="d.id">
            {{ d.roleName }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemUser.form.remark')" prop="remark">
        <a-input v-model="form.remark" :placeholder="$t('views.systemUser.form.placeholderInput')" type="textarea" allow-clear />
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            {{ $t('views.systemUser.form.save') }}
          </a-button>
          <a-button type="dashed" @click="cancel">
            {{ $t('views.systemUser.form.cancel') }}
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>

import { getUser, addUser, updateUser } from '@/api/system/user'

export default {
  name: 'CreateForm',
  props: {
    deptOptions: {
      type: Array,
      required: true
    },
    statusOptions: {
      type: Array,
      required: true
    },
    sexOptions: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      submitLoading: false,
      replaceFields: { children: 'children', title: 'label', key: 'id', value: 'id' },
      postOptions: [],
      roleOptions: [],
      initPassword: undefined,
      formTitle: '',
      form: {
        id: undefined,
        deptId: 0,
        userName: undefined,
        nickName: undefined,
        password: undefined,
        phoneNumber: undefined,
        email: undefined,
        sex: 0,
        status: 1,
        remark: undefined,
        postIds: [],
        roleIds: []
      },
      open: false
    }
  },
  computed: {
    localizedSexOptions () {
      return this.localizeDictOptions(this.sexOptions, 'views.systemUser.dict.sex')
    },
    localizedStatusOptions () {
      return this.localizeDictOptions(this.statusOptions, 'views.systemUser.dict.status')
    },
    rules () {
      return {
        userName: [
          { required: true, message: this.$t('views.systemUser.form.rules.userNameRequired'), trigger: 'blur' }
        ],
        nickName: [
          { required: true, message: this.$t('views.systemUser.form.rules.nickNameRequired'), trigger: 'blur' }
        ],
        deptId: [
          { required: true, message: this.$t('views.systemUser.form.rules.deptRequired'), trigger: 'change' }
        ],
        password: [
          { required: true, message: this.$t('views.systemUser.form.rules.passwordRequired'), trigger: 'blur' },
          { min: 5, max: 20, message: this.$t('views.systemUser.form.rules.passwordLength'), trigger: 'blur' }
        ],
        email: [
          { required: true, message: this.$t('views.systemUser.form.rules.emailRequired'), trigger: 'blur' },
          {
            type: 'email',
            message: this.$t('views.systemUser.form.rules.emailInvalid'),
            trigger: ['blur', 'change']
          }
        ],
        phoneNumber: [
          { required: true, message: this.$t('views.systemUser.form.rules.phoneRequired'), trigger: 'blur' },
          {
            pattern: /^1[3|4|5|6|7|8|9][0-9]\d{8}$/,
            message: this.$t('views.systemUser.form.rules.phoneInvalid'),
            trigger: 'blur'
          }
        ]
      }
    }
  },
  created () {
    this.getConfigKey('sys.user.initPassword').then(response => {
      this.initPassword = response.msg
    })
  },
  methods: {
    localizeDictOptions (options, keyPrefix) {
      return (options || []).map(item => {
        const i18nKey = `${keyPrefix}.${item.value}`
        return {
          ...item,
          label: this.$te(i18nKey) ? this.$t(i18nKey) : item.label
        }
      })
    },
    onClose () {
      this.open = false
    },
    cancel () {
      this.open = false
      this.reset()
    },
    reset () {
      this.form = {
        id: undefined,
        deptId: undefined,
        userName: undefined,
        nickName: undefined,
        password: undefined,
        phoneNumber: undefined,
        email: undefined,
        sex: 0,
        status: 1,
        remark: undefined,
        postIds: [],
        roleIds: []
      }
    },
    handleAdd () {
      this.reset()
      this.$emit('select-tree')
      getUser().then(response => {
        this.postOptions = response.data.posts
        this.roleOptions = response.data.roles
        this.open = true
        this.formTitle = this.$t('views.systemUser.form.titleAdd')
        this.form.password = this.initPassword
      })
    },
    handleUpdate (row, ids) {
      this.reset()
      this.$emit('select-tree')
      const userId = row ? row.id : ids
      getUser(userId).then(response => {
        this.form = response.data.data
        this.postOptions = response.data.posts
        this.roleOptions = response.data.roles
        this.form.postIds = response.data.postIds
        this.form.roleIds = response.data.roleIds
        this.open = true
        this.formTitle = this.$t('views.systemUser.form.titleEdit')
        this.form.password = undefined
      })
    },
    submitForm: function () {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.submitLoading = true
          if (this.form.id !== undefined) {
            updateUser(this.form).then(() => {
              this.$message.success(this.$t('views.systemUser.msg.updateSuccess'), 3)
              this.open = false
              this.$emit('ok')
            }).finally(() => {
              this.submitLoading = false
            })
          } else {
            addUser(this.form).then(() => {
              this.$message.success(this.$t('views.systemUser.msg.addSuccess'), 3)
              this.open = false
              this.$emit('ok')
            }).finally(() => {
              this.submitLoading = false
            })
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>
